/**
 * Polyfill for Intl.Locale/weekInfo to ensure proper locale support across browsers.
 *
 * Why this exists:
 * - MUI's AdapterLuxon relies on Luxon for week calculations.
 * - Luxon uses Intl locale data to determine week settings (start of week, weekend days, etc.).
 * - Some environments have Intl.Locale but *do not* implement Intl.Locale.prototype.weekInfo.
 *   In those cases, Luxon often falls back to ISO weeks (Mon-first) even when the locale is en-US.
 */

import { shouldPolyfill } from "@formatjs/intl-locale/should-polyfill";
import { Settings, type WeekSettings } from "luxon";

// Luxon's WeekSettings uses weekday numbers 1-7 (Mon=1 ... Sun=7).
type LuxonWeekdayNumber = WeekSettings["firstDay"];

function isLuxonWeekdayNumber(n: number): n is LuxonWeekdayNumber {
  return Number.isInteger(n) && n >= 1 && n <= 7;
}

interface IntlWeekInfo {
  firstDay: number;
  minimalDays: number;
  weekend: number[];
}

function getIntlLocaleCtor(): (new (tag: string) => { weekInfo?: IntlWeekInfo }) | null {
  const maybeIntl = (globalThis as unknown as { Intl?: unknown }).Intl;
  const maybeLocale = (maybeIntl as { Locale?: unknown } | undefined)?.Locale;
  if (typeof maybeLocale === "function") {
    return maybeLocale as new (tag: string) => { weekInfo?: IntlWeekInfo };
  }
  return null;
}

function hasWeekInfo(localeTag: string): boolean {
  try {
    const LocaleCtor = getIntlLocaleCtor();
    if (!LocaleCtor) {
      return false;
    }
    const locale = new LocaleCtor(localeTag);
    const weekInfo = locale.weekInfo;
    return !!(weekInfo && typeof weekInfo.firstDay === "number");
  } catch {
    return false;
  }
}

function shouldPolyfillWeekInfo(localeTag: string): boolean {
  // We want weekInfo specifically; some runtimes have Intl.Locale but not weekInfo.
  return !hasWeekInfo(localeTag);
}

function getLuxonWeekSettingsFromIntl(localeTag: string): WeekSettings | null {
  try {
    const LocaleCtor = getIntlLocaleCtor();
    if (!LocaleCtor) {
      return null;
    }

    const weekInfo = new LocaleCtor(localeTag).weekInfo;
    if (!weekInfo) {
      return null;
    }

    const { firstDay, minimalDays, weekend } = weekInfo;
    if (!isLuxonWeekdayNumber(firstDay)) {
      return null;
    }
    if (!isLuxonWeekdayNumber(minimalDays)) {
      return null;
    }
    if (!Array.isArray(weekend)) {
      return null;
    }

    const weekendTyped = weekend.filter(isLuxonWeekdayNumber);
    if (weekendTyped.length !== weekend.length) {
      return null;
    }

    return { firstDay, minimalDays, weekend: weekendTyped };
  } catch {
    return null;
  }
}

const sundayFirstWeekSettings: WeekSettings = {
  firstDay: 7, // Sunday
  minimalDays: 1,
  weekend: [6, 7], // Saturday/Sunday
};

export async function setupIntlLocalePolyfill(): Promise<void> {
  const browserLocale =
    Settings.defaultLocale ??
    (typeof navigator !== "undefined" && navigator?.language ? navigator.language : undefined) ??
    "en-US";

  // Load the polyfill if Intl.Locale is missing OR if Intl.Locale.weekInfo is missing.
  // The second case is important for correct "first day of week" behavior (US vs EU, etc.).
  if (shouldPolyfill() || shouldPolyfillWeekInfo(browserLocale)) {
    await import("@formatjs/intl-locale/polyfill");
  }

  // After ensuring weekInfo exists, derive Luxon's default week settings from the active locale.
  // This makes week layout deterministic for AdapterLuxon even in environments without native weekInfo.
  const weekSettings = getLuxonWeekSettingsFromIntl(browserLocale);
  const localeLower = browserLocale.toLowerCase();
  const baseLocale = localeLower.split("-u-")[0];
  const sundayFirstLocales = new Set(["en-us"]);

  // Force Sunday-first for known locales (e.g., US), regardless of weekInfo quirks.
  if (sundayFirstLocales.has(baseLocale)) {
    Settings.defaultWeekSettings = sundayFirstWeekSettings;
    return;
  }

  if (weekSettings) {
    Settings.defaultWeekSettings = weekSettings;
    return;
  }

  // Fallback: default to Sunday-first if weekInfo is still unavailable.
  Settings.defaultWeekSettings = sundayFirstWeekSettings;
}
